import { readNullableString, readNumber, readOneOf, readRecord, readString, readStringList } from '@/lib/readers';
import type { LiveRow } from '@/lib/pendingWrites';

/** Every kind of template the hub stores. */
export const TEMPLATE_TYPES = ['html5'] as const;
export type TemplateType = (typeof TEMPLATE_TYPES)[number];

/** Every format a template may declare; the backend maps the archive's ad.size to the closest one. */
export const TEMPLATE_FORMATS = ['1:1', '4:5', '9:16', '16:9', '1.91:1', '4:1', '2:3'] as const;

/** Where a template came from, which decides whether it has an archive or a document. */
export const TEMPLATE_SOURCES = ['upload', 'editor'] as const;
export type TemplateSource = (typeof TEMPLATE_SOURCES)[number];

export interface TemplateSize {
  width: number;
  height: number;
}

export interface Template extends LiveRow {
  id: string;
  name: string;
  templateType: TemplateType;
  source: TemplateSource;
  /** The uploaded archive. Null for a template authored in the editor. */
  fileObject: string | null;
  /** The editor document. Null for an uploaded archive. */
  documentId: string | null;
  tags: string[];
  format: string;
  size: TemplateSize | null;
  /** Size variants the document holds; an upload is always one. */
  sizeCount: number;
  /** Distinct dynamic tags the document uses; an upload is always none. */
  fieldCount: number;
  updatedAt: string | null;
}

/** What inspect reads from the archive's ad.size meta tag. Null means it could not tell. */
export interface TemplateInspection {
  format: string | null;
  width: number | null;
  height: number | null;
}

function nullableNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** Returns the size, or null when the width or the height is not a number. */
export function parseTemplateSize(raw: unknown): TemplateSize | null {
  const size = readRecord(raw);
  const width = nullableNumber(size.width);
  const height = nullableNumber(size.height);
  return width !== null && height !== null ? { width, height } : null;
}

/** Returns what inspect read from the archive. A value it could not read is null. */
export function parseTemplateInspection(raw: Record<string, unknown>): TemplateInspection {
  return {
    format: readNullableString(raw.format),
    width: nullableNumber(raw.width),
    height: nullableNumber(raw.height),
  };
}

/** Returns one template, or null when the row has no id or no name. An absent source reads as upload. */
export function parseTemplate(id: string, raw: Record<string, unknown>): Template | null {
  const name = readString(raw.name);
  if (!id || !name) return null;
  const source = readOneOf(TEMPLATE_SOURCES, raw.source, 'upload');
  return {
    id,
    name,
    templateType: readOneOf(TEMPLATE_TYPES, raw.template_type, 'html5'),
    source,
    fileObject: source === 'upload' ? readString(raw.file_object) : null,
    documentId: source === 'editor' ? readString(raw.document_id) || id : null,
    tags: readStringList(raw.tags),
    format: readString(raw.format),
    size: parseTemplateSize(raw.size),
    sizeCount: Math.max(1, readNumber(raw.size_count, 1)),
    fieldCount: Math.max(0, readNumber(raw.field_count)),
    updatedAt: readNullableString(readRecord(raw.metadata).updated_at),
  };
}
